import Modal from "@/components/Modal";
import Input from "@/components/form/Input";
import { useAppDispatch } from "@/hooks";
import { updateProject } from "@/store/slices/projectSlice";
import { ProjectList } from "@/types/project.model";
import {
  HOURS_DECIMAL_PATTERN,
  HOURS_DECIMAL_PATTERN_MESSAGE,
  sanitizeDecimalHoursInput,
} from "@/utils/hoursInput";
import React from "react";
import { useForm } from "react-hook-form";

interface AddMoreTimeFormData {
  additionalHours: string;
  revisedEstimate: string;
}

interface AddMoreTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectList;
  isLoading?: boolean;
}

const AddMoreTimeModal: React.FC<AddMoreTimeModalProps> = ({
  isOpen,
  onClose,
  project,

  isLoading = false,
}) => {
  // Initialize form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
  } = useForm<AddMoreTimeFormData>({
    defaultValues: {
      additionalHours: "",
      revisedEstimate: "",
    },
    mode: "onChange",
  });

  const dispatch = useAppDispatch();

  // Watch for changes in additionalHours to update revisedEstimate
  const additionalHours = watch("additionalHours");

  // Calculate revised estimate whenever additionalHours changes
  React.useEffect(() => {
    if (additionalHours) {
      try {
        const remainingHours = parseFloat(
          project.remi_hrs !== null ? project.remi_hrs.toString() : "0"
        );
        const additional = parseFloat(additionalHours);
        if (!isNaN(additional) && additional > 0) {
          const revised = remainingHours + additional;
          setValue("revisedEstimate", revised.toFixed(2));
        }
      } catch (error) {
        console.error("Error calculating revised estimate:", error);
      }
    } else {
      setValue("revisedEstimate", "");
    }
  }, [additionalHours, project.remi_hrs, setValue]);

  // Form submission handler
  const onFormSubmit = (data: AddMoreTimeFormData) => {
    // onSubmit(data);

    const updatedProject: any = {
      // ...project,
      id: project?.id,
      // client_id: project.client.id,
      // start_date: moment(project.start_date, "DD/MM/YYYY").format("DDMMYYYY"),
      // end_date: moment(project.end_date, "DD/MM/YYYY").format("DDMMYYYY"),
      esti_hrs: parseFloat(data.additionalHours),
    };

    delete updatedProject.client;
    dispatch(updateProject(updatedProject))
      .unwrap()
      .then(() => {
        onModalClose();
      });
  };

  const onModalClose = () => {
    onClose();
    setValue("additionalHours", "");
    setValue("revisedEstimate", "");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onModalClose}
      title="Add more time"
      width="min(95%, 868px)"
      contentClassName="p-0"
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="mb-5 px-3 sm:px-4 py-2 overflow-y-auto max-h-[calc(100vh-200px)]">
          <p className="text-[#6C6C6C] text-[14px] mb-4">
            <span className="text-[#DC2626]">*</span>Indicates mandatory fields
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <div>
              <Input
                label="Remaining hours"
                type="text"
                value={project.remi_hrs !== null ? project.remi_hrs : "0"}
                readOnly
              />
            </div>
            <div>
              <Input
                label="Additional purchased hours"
                required
                placeholder="Enter hours"
                error={errors.additionalHours?.message}
                maxLength={10}
                onInput={sanitizeDecimalHoursInput}
                {...register("additionalHours", {
                  required: {
                    value: true,
                    message: "Additional hours is required",
                  },
                  pattern: {
                    value: HOURS_DECIMAL_PATTERN,
                    message: HOURS_DECIMAL_PATTERN_MESSAGE,
                  },
                })}
              />
            </div>
          </div>

          <div className="mb-6">
            <Input
              label="Revised estimate"
              placeholder="Enter revised estimate"
              readOnly
              error={errors.revisedEstimate?.message}
              {...register("revisedEstimate", {
                pattern: {
                  value: HOURS_DECIMAL_PATTERN,
                  message: HOURS_DECIMAL_PATTERN_MESSAGE,
                },
                validate: (value) => {
                  if (!value) return true;
                  const num = parseFloat(value);
                  return num > 0 || "Estimate must be greater than 0";
                },
              })}
            />
          </div>
        </div>
        <div className="px-3 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={onModalClose}
            className="px-4 py-2 text-[14px] w-full sm:w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer order-1 sm:order-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-full sm:w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[6px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed mb-2 sm:mb-0"
            disabled={!isValid || isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </>
            ) : (
              "Update time"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddMoreTimeModal;
